import { useEffect, useState } from "react"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import {
  getAdminTenderHandoffConfig,
  sendAdminTenderHandoffTestEmail,
  type TenderHandoffConfig,
  type TenderHandoffSummaryLayout,
  updateAdminTenderHandoffConfig,
  verifyAdminTenderHandoffConfig,
} from "@/api/admin/tender"
import { AlertTriangle, Check, Mail, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Spinner } from "@/components/ui/spinner"
import { Textarea } from "@/components/ui/textarea"
import { useRegisterAdminDirty } from "@/features/admin-dashboard/admin-unsaved-changes-context"

import { LocalAiLeadGroupPicker } from "./local-ai-lead-group-picker"
import { isValidSecretRefInput, toSecretRef } from "./secret-ref-utils"

const QUERY_KEY = ["admin", "tender-handoff-config"] as const

type VerifyResult = { secretValid: boolean; tokenValid: boolean; error?: string }

function CheckRow({ ok, label }: { ok: boolean; label: string }) {
  return (
    <div className="flex items-center gap-1.5 text-xs">
      {ok ? (
        <Check className="text-success size-3.5" />
      ) : (
        <X className="text-destructive size-3.5" />
      )}
      <span className={ok ? "text-success" : "text-destructive"}>{label}</span>
    </div>
  )
}

// Content block for the handoff config (group + dedicated mailbox). Rendered as a
// sub-section inside the single "Tender" admin section, so it has no collapsible
// wrapper of its own.
export function TenderHandoffConfigContent() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const configQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: getAdminTenderHandoffConfig,
  })

  const [groupId, setGroupId] = useState<string | null>(null)
  const [groupName, setGroupName] = useState<string | null>(null)
  const [platformAccessGroupId, setPlatformAccessGroupId] = useState<string | null>(null)
  const [platformAccessGroupName, setPlatformAccessGroupName] = useState<string | null>(null)
  const [senderEmail, setSenderEmail] = useState("")
  const [clientId, setClientId] = useState("")
  const [tenantId, setTenantId] = useState("")
  // Holds the masked value (***configured***) when a secret is stored, or a freshly
  // typed Key Vault secret name. Empty clears the secret; masked preserves it.
  const [clientSecret, setClientSecret] = useState("")
  const [routinesTitle, setRoutinesTitle] = useState("")
  const [routinesText, setRoutinesText] = useState("")
  const [summaryLayout, setSummaryLayout] = useState<TenderHandoffSummaryLayout>("trimmed")
  const [dirty, setDirty] = useState(false)
  useRegisterAdminDirty("tender-handoff", dirty)
  const [verifyResult, setVerifyResult] = useState<VerifyResult | null>(null)

  // Seed local state from the loaded config (once it arrives / refetches).
  useEffect(() => {
    const c = configQuery.data?.tender_handoff_config
    if (!c) return
    setGroupId(c.user_group_id ?? null)
    setGroupName(c.user_group_name ?? null)
    setPlatformAccessGroupId(c.platform_access_group_id ?? null)
    setPlatformAccessGroupName(c.platform_access_group_name ?? null)
    setSenderEmail(c.sender_email ?? "")
    setClientId(c.client_id ?? "")
    setTenantId(c.tenant_id ?? "")
    setClientSecret(c.client_secret ?? "")
    setRoutinesTitle(c.routines_title ?? "")
    setRoutinesText(c.routines_text ?? "")
    // Anything other than an explicit "full" (missing, null, unknown) is the default.
    setSummaryLayout(c.summary_layout === "full" ? "full" : "trimmed")
    setDirty(false)
    setVerifyResult(null)
  }, [configQuery.data])

  const saveMutation = useMutation({
    mutationFn: (config: TenderHandoffConfig) => updateAdminTenderHandoffConfig(config),
    onSuccess: () => {
      toast.success(t("adminDashboard.tenderHandoff.saved", "Handoff settings saved"))
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY })
      setDirty(false)
    },
    onError: () => {
      toast.error(t("adminDashboard.tenderHandoff.saveError", "Failed to save handoff settings"))
    },
  })

  // The backend stores (and verifies) a Key Vault REFERENCE, so the typed name is
  // turned into "@kv:<name>" here; an unusable name is refused before any request.
  const secretRefValid = isValidSecretRefInput(clientSecret)

  const verifyMutation = useMutation({
    mutationFn: () =>
      verifyAdminTenderHandoffConfig({
        client_id: clientId.trim() || null,
        tenant_id: tenantId.trim() || null,
        client_secret: toSecretRef(clientSecret),
      }),
    onSuccess: (result) => setVerifyResult(result),
    onError: () =>
      setVerifyResult({
        secretValid: false,
        tokenValid: false,
        error: t("adminDashboard.tenderHandoff.verifyFailed", "Verification request failed"),
      }),
  })

  const testEmailMutation = useMutation({
    mutationFn: sendAdminTenderHandoffTestEmail,
    onSuccess: (result) => {
      if (result.sent) {
        toast.success(
          t("adminDashboard.tenderHandoff.testEmailSent", "Test email sent to {{recipient}}", {
            recipient: result.recipient ?? "",
          })
        )
      } else {
        toast.error(
          result.error ??
            t("adminDashboard.tenderHandoff.testEmailFailed", "Could not send the test email")
        )
      }
    },
    onError: () =>
      toast.error(
        t("adminDashboard.tenderHandoff.testEmailFailed", "Could not send the test email")
      ),
  })

  // The mailbox sends only when every field is filled in (a sender, the app
  // registration IDs, and a referenced Key Vault secret). When anything is missing
  // we warn that handoffs won't email — there's no separate on/off toggle anymore.
  const testEmailDirtyHint = t(
    "adminDashboard.tenderHandoff.sendTestEmailDirtyHint",
    "Save your changes first — the test email uses the saved settings"
  )

  const mailboxConfigured =
    senderEmail.trim() !== "" &&
    clientId.trim() !== "" &&
    tenantId.trim() !== "" &&
    clientSecret.trim() !== ""

  const handleSave = () => {
    if (!secretRefValid) return
    saveMutation.mutate({
      user_group_id: groupId,
      user_group_name: groupName,
      platform_access_group_id: platformAccessGroupId?.trim() || null,
      platform_access_group_name: platformAccessGroupId?.trim() ? platformAccessGroupName : null,
      email_enabled: mailboxConfigured,
      sender_email: senderEmail.trim() || null,
      client_id: clientId.trim() || null,
      tenant_id: tenantId.trim() || null,
      // Masked → preserve; empty → clear; a name → stored as @kv:<name>.
      client_secret: toSecretRef(clientSecret),
      routines_title: routinesTitle.trim() || null,
      routines_text: routinesText.trim() || null,
      summary_layout: summaryLayout,
    })
  }

  const markDirty = () => {
    setDirty(true)
    setVerifyResult(null)
  }

  return (
    <div className="space-y-4" data-admin-dirty={dirty ? "true" : undefined}>
      <div>
        <h3 className="text-sm font-semibold">
          {t("adminDashboard.tenderHandoff.title", "Tender handoff")}
        </h3>
      </div>
      {configQuery.isLoading ? (
        <div className="text-muted-foreground flex items-center gap-2 text-sm">
          <Spinner className="size-4" />
          {t("common.loading", "Loading…")}
        </div>
      ) : (
        <div className="max-w-2xl space-y-6">
          <p className="text-muted-foreground text-sm">
            {t(
              "adminDashboard.tenderHandoff.description",
              "Configure who can be picked as a handoff recipient and the dedicated mailbox the hand-over email is sent from. Enter the app registration's IDs and the NAME of the Key Vault secret holding its client secret — the secret value itself is set in Key Vault ahead of time and only referenced here."
            )}
          </p>

          {/* Recipient-picker scope group */}
          {/* The picker renders its "enter ID manually" link in a zero-height
              box, so give each picker room below it before the next field. */}
          <div className="pb-4">
            <LocalAiLeadGroupPicker
              groupId={groupId}
              groupName={groupName}
              id="tender-handoff-recipient-group"
              label={t("adminDashboard.tenderHandoff.recipientGroup")}
              onChange={(id, name) => {
                setGroupId(id)
                setGroupName(name)
                setDirty(true)
              }}
            />
          </div>

          {/* The Entra group that actually grants platform access. Only used to
              decide whether a summary-copy recipient gets a case link; it does
              not restrict who can receive the summary. Same picker as the
              recipient group above so admins search by name instead of pasting
              an object ID. */}
          <div className="space-y-2">
            <div className="pb-4">
              <LocalAiLeadGroupPicker
                groupId={platformAccessGroupId}
                groupName={platformAccessGroupName}
                id="tender-platform-access-group"
                label={t("adminDashboard.tenderHandoff.platformAccessGroup")}
                noGroupLabel={t("adminDashboard.tenderHandoff.platformAccessGroupNone")}
                onChange={(id, name) => {
                  setPlatformAccessGroupId(id)
                  setPlatformAccessGroupName(name)
                  markDirty()
                }}
              />
            </div>
            <p className="text-muted-foreground text-xs">
              {t("adminDashboard.tenderHandoff.platformAccessGroupHint")}
            </p>
          </div>

          {/* Dedicated handoff mailbox */}
          <div className="space-y-4 border-t pt-4">
            {!mailboxConfigured && (
              <div className="border-destructive/30 bg-destructive/10 text-destructive flex items-start gap-2 rounded-md border px-3 py-2 text-xs">
                <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                <span>
                  {t(
                    "adminDashboard.tenderHandoff.notConfiguredWarning",
                    "Mail sender not configured — handoffs still complete, but no hand-over emails are sent."
                  )}
                </span>
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="tender-handoff-sender">
                {t("adminDashboard.tenderHandoff.senderLabel", "Sender address")}
              </Label>
              <Input
                autoComplete="off"
                id="tender-handoff-sender"
                placeholder="tender@example.com"
                type="email"
                value={senderEmail}
                onChange={(e) => {
                  setSenderEmail(e.target.value)
                  markDirty()
                }}
              />
            </div>

            {/* App registration */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="tender-handoff-client-id">
                  {t("adminDashboard.tenderHandoff.clientIdLabel", "Application (client) ID")}
                </Label>
                <Input
                  autoComplete="off"
                  className="font-mono text-sm"
                  id="tender-handoff-client-id"
                  placeholder="00000000-0000-0000-0000-000000000000"
                  value={clientId}
                  onChange={(e) => {
                    setClientId(e.target.value)
                    markDirty()
                  }}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="tender-handoff-tenant-id">
                  {t("adminDashboard.tenderHandoff.tenantIdLabel", "Tenant ID")}
                </Label>
                <Input
                  autoComplete="off"
                  className="font-mono text-sm"
                  id="tender-handoff-tenant-id"
                  placeholder="00000000-0000-0000-0000-000000000000"
                  value={tenantId}
                  onChange={(e) => {
                    setTenantId(e.target.value)
                    markDirty()
                  }}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="tender-handoff-secret">
                {t(
                  "adminDashboard.tenderHandoff.secretLabel",
                  "Client secret (Key Vault secret name)"
                )}
              </Label>
              <Input
                autoComplete="off"
                className="font-mono text-sm"
                id="tender-handoff-secret"
                value={clientSecret}
                placeholder={t(
                  "adminDashboard.tenderHandoff.secretPlaceholder",
                  "e.g. tender-handoff-mail-secret"
                )}
                onChange={(e) => {
                  setClientSecret(e.target.value)
                  markDirty()
                }}
              />
              <p className="text-muted-foreground text-xs">
                {t(
                  "adminDashboard.tenderHandoff.secretHint",
                  "The secret value lives in Key Vault — enter only its name. Leave the mask to keep the current secret."
                )}
              </p>
              {/* Say why a name cannot become a reference before the save is tried:
                  the backend answers anything but @kv:<name> with a 400. */}
              {!secretRefValid && (
                <p className="text-destructive text-xs" data-testid="tender-handoff-secret-error">
                  {t(
                    "adminDashboard.secretRef.invalidName",
                    "Use letters, digits and hyphens only — enter the Key Vault secret name, not its value."
                  )}
                </p>
              )}
            </div>

            {/* Verify + test */}
            <div className="flex flex-wrap items-center gap-3">
              <Button
                disabled={verifyMutation.isPending || !secretRefValid}
                size="sm"
                variant="outline"
                onClick={() => verifyMutation.mutate()}
              >
                {verifyMutation.isPending && <Spinner className="mr-2 size-4" />}
                {t("adminDashboard.tenderHandoff.verify", "Verify")}
              </Button>
              {/* Disabled while dirty: unlike Verify (which posts the values
                  currently typed), the test email is rendered server-side from
                  the SAVED config — so with unsaved edits it would preview the
                  old routines text, which is the exact opposite of what
                  "tweak the text, send myself a test" is asking for. */}
              <Button
                disabled={testEmailMutation.isPending || dirty}
                size="sm"
                title={dirty ? testEmailDirtyHint : undefined}
                variant="outline"
                onClick={() => testEmailMutation.mutate()}
              >
                {testEmailMutation.isPending ? (
                  <Spinner className="mr-2 size-4" />
                ) : (
                  <Mail className="mr-2 size-4" />
                )}
                {t("adminDashboard.tenderHandoff.sendTestEmail", "Send test email")}
              </Button>
            </div>
            {dirty && <p className="text-muted-foreground text-xs">{testEmailDirtyHint}</p>}

            {verifyResult && (
              <div className="space-y-1">
                <CheckRow
                  ok={verifyResult.secretValid}
                  label={
                    verifyResult.secretValid
                      ? t("adminDashboard.tenderHandoff.secretOk", "Key Vault secret found")
                      : t("adminDashboard.tenderHandoff.secretBad", "Key Vault secret not found")
                  }
                />
                <CheckRow
                  ok={verifyResult.tokenValid}
                  label={
                    verifyResult.tokenValid
                      ? t("adminDashboard.tenderHandoff.tokenOk", "Authentication succeeded")
                      : t("adminDashboard.tenderHandoff.tokenBad", "Authentication failed")
                  }
                />
                {verifyResult.error && (
                  <p className="text-destructive text-xs">{verifyResult.error}</p>
                )}
              </div>
            )}
          </div>

          {/* Static bid-routines block shown near the top of the hand-over email */}
          <div className="space-y-4 border-t pt-4">
            <div className="space-y-1">
              <h4 className="text-sm font-medium">
                {t("adminDashboard.tenderHandoff.routinesTitle", "Bid routines in the email")}
              </h4>
              <p className="text-muted-foreground text-xs">
                {t(
                  "adminDashboard.tenderHandoff.routinesDescription",
                  'A static block (e.g. your internal bid-routines checklist) shown near the top of every hand-over email. Leave empty to omit it. Lines starting with "1." or "-" render as a list.'
                )}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tender-handoff-routines-title">
                {t("adminDashboard.tenderHandoff.routinesHeadingLabel", "Heading")}
              </Label>
              <Input
                autoComplete="off"
                id="tender-handoff-routines-title"
                maxLength={200}
                value={routinesTitle}
                placeholder={t(
                  "adminDashboard.tenderHandoff.routinesHeadingPlaceholder",
                  "Anbudsrutiner"
                )}
                onChange={(e) => {
                  setRoutinesTitle(e.target.value)
                  markDirty()
                }}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tender-handoff-routines-text">
                {t("adminDashboard.tenderHandoff.routinesTextLabel", "Content")}
              </Label>
              <Textarea
                id="tender-handoff-routines-text"
                maxLength={8000}
                rows={8}
                value={routinesText}
                placeholder={t(
                  "adminDashboard.tenderHandoff.routinesTextPlaceholder",
                  "1. Har vi kapacitet att ta hand om anbud?\n2. Har vi resurser att ta hand om uppdraget vid eventuell vinst?\n…"
                )}
                onChange={(e) => {
                  setRoutinesText(e.target.value)
                  markDirty()
                }}
              />
            </div>
          </div>

          {/* Which summary layout the hand-over email renders */}
          <div className="space-y-2 border-t pt-4">
            <Label htmlFor="tender-handoff-summary-layout">
              {t("adminDashboard.tenderHandoff.layoutLabel", "Summary layout in the email")}
            </Label>
            <Select
              value={summaryLayout}
              onValueChange={(value) => {
                setSummaryLayout(value === "full" ? "full" : "trimmed")
                markDirty()
              }}
            >
              <SelectTrigger id="tender-handoff-summary-layout">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="trimmed">
                  {t("adminDashboard.tenderHandoff.layoutTrimmed", "Trimmed (new default)")}
                </SelectItem>
                <SelectItem value="full">
                  {t("adminDashboard.tenderHandoff.layoutFull", "Full (previous layout)")}
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-muted-foreground text-xs">
              {t(
                "adminDashboard.tenderHandoff.layoutHint",
                "Trimmed follows the reference group's structure. Full is the previous, longer variant."
              )}
            </p>
          </div>

          <div className="flex justify-end">
            <Button
              disabled={!dirty || saveMutation.isPending || !secretRefValid}
              onClick={handleSave}
            >
              {saveMutation.isPending && <Spinner className="mr-2 size-4" />}
              {t("common.save", "Save")}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
